import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import TicTacToe from "@/components/games/TicTacToe";
import Connect4 from "@/components/games/Connect4";
import RPS from "@/components/games/RPS";
import Hangman from "@/components/games/Hangman";
import WordScramble from "@/components/games/WordScramble";
import DiceRoll from "@/components/games/DiceRoll";
import CoinFlip from "@/components/games/CoinFlip";
import Slots from "@/components/games/Slots";
import Checkers from "@/components/games/Checkers";
import ChessGame from "@/components/games/ChessGame";
import Pinball from "@/components/games/Pinball";
import PaddleClash from "@/components/games/PaddleClash";
import NeonCycles from "@/components/games/NeonCycles";
import { Lock, ArrowLeft, Radio, Users } from "lucide-react";
import useGameTick from "@/lib/useGameTick";

export default function Games() {
  const [catalog, setCatalog] = useState([]);
  const [liveCounts, setLiveCounts] = useState({});
  const [slug, setSlug] = useState(null);
  const [streamers, setStreamers] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // Accepting a game invite: /games?join_room=XXX&slug=pong
  const joinRoomId = searchParams.get("join_room");
  const joinSlug   = searchParams.get("slug");
  const spectate   = searchParams.get("spectate") === "1";

  useGameTick(slug); // awards 1 Spark/min while a single-player game session is open

  useEffect(() => {
    api.get("/games/catalog").then(r => setCatalog(r.data));
    api.get("/games/live_counts").then(r => setLiveCounts(r.data));
  }, []);

  // On mount (or when the URL params change), auto-jump into the invited game
  useEffect(() => {
    if (joinRoomId && joinSlug) setSlug(joinSlug);
  }, [joinRoomId, joinSlug]);

  useEffect(() => {
    if (!slug) return;
    api.get(`/games/${slug}/streamers`).then(r => setStreamers(r.data)).catch(() => setStreamers([]));
  }, [slug]);

  const renderGame = () => {
    switch (slug) {
      case "ttt": return <TicTacToe />;
      case "connect4": return <Connect4 />;
      case "rps": return <RPS />;
      case "hangman": return <Hangman />;
      case "wordscramble": return <WordScramble />;
      case "diceroll": return <DiceRoll />;
      case "coinflip": return <CoinFlip />;
      case "slots": return <Slots />;
      case "checkers": return <Checkers />;
      case "chess": return <ChessGame />;
      case "pinball": return <Pinball />;
      case "pong": return <PaddleClash joinRoomId={joinRoomId} spectate={spectate} />;
      case "tron": return <NeonCycles joinRoomId={joinRoomId} spectate={spectate} />;
      default: return null;
    }
  };

  if (slug) {
    const meta = catalog.find(g => g.slug === slug);
    return (
      <div className="max-w-4xl mx-auto px-6 py-10">
        <button onClick={() => { setSlug(null); if (joinRoomId) setSearchParams({}); }} className="sc-btn-ghost text-xs mb-6 inline-flex items-center gap-2">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to lobby
        </button>
        <div className="sc-label text-[#00E5FF] mb-2">
          {meta?.players === "2" ? "Hot-Seat · 2 players, one device" : "Solo Play"}
        </div>
        {renderGame()}

        {/* Streamers playing this */}
        <div className="mt-10">
          <div className="flex items-center gap-2 mb-4">
            <Radio className="w-4 h-4 text-[#FF3366]" />
            <div className="sc-label">Live with {meta?.name}</div>
            <span className="text-xs text-zinc-500">{streamers.length} streamer{streamers.length === 1 ? "" : "s"}</span>
          </div>
          {streamers.length === 0 ? (
            <div className="border border-dashed border-white/10 rounded-sm p-6 text-center">
              <div className="text-sm text-zinc-300">No streamers live with this game right now.</div>
              <div className="text-xs text-zinc-500 mt-1">Be the first — go live on your own channel and pick this game in your lobby.</div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {streamers.map(s => (
                <Link
                  key={s.id}
                  to={`/c/${s.username}`}
                  data-testid={`game-streamer-${s.username}`}
                  className="sc-card p-4 flex items-center justify-between"
                >
                  <div>
                    <div className="text-sm font-semibold">{s.username}</div>
                    <div className="text-xs text-zinc-400 mt-1 line-clamp-1">{s.title}</div>
                  </div>
                  <div className="flex items-center gap-1 text-xs px-2 py-1 bg-[#FF3366] rounded-sm font-bold">
                    <Users className="w-3 h-3" /> {s.viewer_count || 0}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const available = catalog.filter(g => g.available && !g.streamer_only);
  const soon = catalog.filter(g => !g.available);

  // Genre → accent color mapping for both Play-now and Coming-Soon tiles.
  const GENRE_ACCENT = {
    "Arcade":   "#CCFF00", // volt
    "Action":   "#FF5900", // orange
    "MMO":      "#A78BFA", // purple
    "Shooter":  "#FF1053", // pink
    "Team":     "#00E5FF", // cyan
    "Card":     "#F59E0B", // gold
    "Board":    "#00E5FF", // cyan
    "Strategy": "#CCFF00", // volt
    "Puzzle":   "#A78BFA", // purple
    "Sports":   "#10B981", // emerald
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
      <div className="sc-label">Play Now</div>
      <h1 className="sc-heading text-4xl sm:text-5xl font-black tracking-tighter mt-1 mb-2">Arena Arcade</h1>
      <p className="text-sm text-zinc-400 mb-8">100% free-to-play. No wagers, no win rewards. You earn <span className="text-[color:var(--sc-cyan)] font-bold">+1 Spark per minute</span> while you&apos;re actively playing — no Sparks if you&apos;re idle or tab is hidden.</p>

      <div className="sc-label text-[#10B981] mb-3">Playable now · {available.length}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-12 stagger">
        {available.map((g) => {
          const liveN = liveCounts[g.slug] || 0;
          const accent = GENRE_ACCENT[g.category] || "#00E5FF";
          return (
            <button
              key={g.slug}
              data-testid={`games-tile-${g.slug}`}
              onClick={() => {
                if (g.external_route) {
                  navigate(g.external_route);
                } else {
                  setSlug(g.slug);
                }
              }}
              className="sc-card p-6 text-left cursor-pointer relative transition hover:-translate-y-0.5"
              style={{ borderColor: `${accent}30` }}
            >
              {liveN > 0 && (
                <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 bg-[#FF3366] rounded-sm text-[10px] tracking-widest uppercase font-bold">
                  <Radio className="w-3 h-3" /> {liveN} live
                </div>
              )}
              <div className="text-[10px] tracking-widest uppercase sc-mono" style={{ color: accent }}>{g.category}</div>
              <div className="sc-heading text-xl font-black mt-2">{g.name}</div>
              <div className="text-xs text-zinc-400 mt-1 flex items-center gap-1"><Users className="w-3 h-3" /> {g.players} players</div>
              <div className="text-[11px] mt-4 tracking-widest uppercase font-bold" style={{ color: accent }}>▶ Play</div>
            </button>
          );
        })}
      </div>

      <div className="sc-label mb-1 flex items-center gap-2">In the pipeline · {soon.length}</div>
      <p className="text-xs text-zinc-500 mb-4 sc-mono">Massively-multiplayer arena games rolling out through 2026. Order below = roadmap priority.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 stagger">
        {soon.map((g, i) => {
          const accent = GENRE_ACCENT[g.category] || "#8A8A94";
          return (
            <div
              key={g.slug}
              data-testid={`games-tile-soon-${g.slug}`}
              className="relative p-5 rounded-sm bg-black/30 border transition group"
              style={{ borderColor: `${accent}25` }}
            >
              <div
                className="absolute inset-0 pointer-events-none opacity-40 rounded-sm"
                style={{ background: `radial-gradient(circle at 100% 0%, ${accent}22, transparent 60%)` }}
              />
              <div className="relative">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] tracking-widest uppercase sc-mono" style={{ color: accent }}>{g.category}</div>
                  <div className="text-[9px] tracking-widest uppercase sc-mono text-zinc-500">#{i + 1}</div>
                </div>
                <div className="sc-heading text-lg font-black mt-2 text-zinc-100">{g.name}</div>
                <div className="text-xs text-zinc-400 mt-1 flex items-center gap-1"><Users className="w-3 h-3" /> {g.players}</div>
                <div className="mt-4 flex items-center gap-1.5 text-[10px] tracking-widest uppercase sc-mono text-zinc-500">
                  <Lock className="w-3 h-3" /> Coming soon
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
